import React from 'react';

import { DatabaseContext } from '../database/DatabaseHandler';
import { ParametersContext } from '../parametersProcessor/ParametersProcessor';
import { CartContext } from '../cart/CartHandler';

/**
 * The ucon + StyleScript pair the Designer shows when it is not the product's own, kept for the
 * tab's other Designer pages (/designer, /designeradmin, /ai-designer): a room the AI designer made
 * is still there on /designeradmin to quote, and an admin's edit is there back on /ai-designer.
 * sessionStorage, as the AI designer's last conversation: every tab has its own, a new tab starts
 * from the product. The scripts are text, as the AI designer page holds them; `source` and
 * `revision` are the AI designer page's (IScriptPair), the other pages leave them out.
 */
export interface IShownDesign {
    ucon: string;
    style: string;
    source?: 'ai' | 'manual' | 'scan' | 'revision';
    revision?: number;
}

const KEY = 'ucDesigner.shown';

/** Keeps the pair shown, or forgets it (null): the product is shown. */
export function keepShownDesign(design: IShownDesign | null) {
    try {
        if (design) sessionStorage.setItem(KEY, JSON.stringify(design));
        else sessionStorage.removeItem(KEY);
    } catch {
        // no storage (a private window) or a room too big for it: the next page shows the product
    }
}

/** The pair a page of this tab showed last; null for the product. */
export function shownDesign(): IShownDesign | null {
    try {
        const kept = JSON.parse(sessionStorage.getItem(KEY) ?? 'null');
        return typeof kept?.ucon === 'string' && typeof kept?.style === 'string' ? kept : null;
    } catch {
        return null;
    }
}

const parse = (text: string): unknown => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

/**
 * The Designer and the admin Designer page's side (the AI designer page's is in AiDesignerProvider):
 * the cart's script design (CartHandler.adminDesign) is the pair shown. The pair a page of this tab
 * left opens as one, as a cart line saved from scripts does (CartHandler.restoreDesignScripts), so
 * "Kosárba" and an Árajánlat store it. A quote or a shared cart opened by a link shows its own.
 */
const ShownDesignKeeper: React.FC = () => {
    const data = React.useContext(DatabaseContext);
    const params = React.useContext(ParametersContext);
    const cart = React.useContext(CartContext);
    // Read before the cart's loaders take the link's parameters off the address
    const linked = React.useRef(['quote', 'share'].some((key) => new URLSearchParams(window.location.search).has(key)));
    // The pair opened, and its scripts as this page writes them: unchanged, it is kept as it came
    const opened = React.useRef<{ design: IShownDesign; ucon: string; style: string } | null>(null);
    const [ready, setReady] = React.useState(false);

    // Once the product's scripts are in: a later fetch of them would replace the design's ucon.
    React.useEffect(() => {
        if (ready || data.uconScript == null || data.paramScripts == null) return;
        setReady(true);
        const design = linked.current || cart.isQuote ? null : shownDesign();
        if (!design) return;
        const ucon = parse(design.ucon);
        const style = parse(design.style);
        if (ucon == null || !Array.isArray(style)) return;
        opened.current = { design, ucon: JSON.stringify(ucon), style: JSON.stringify(style) };
        cart.setDesignStyle(style);
        cart.setAdminDesign(true);
        data.updateScripts(data.paramScripts, ucon);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.uconScript, data.paramScripts]);

    React.useEffect(() => {
        if (!ready) return;
        if (!cart.adminDesign || data.uconScript == null) {
            keepShownDesign(null);
            return;
        }
        const ucon = JSON.stringify(data.uconScript);
        const style = JSON.stringify(cart.designStyle ?? params.generateStyleScript());
        const same = opened.current && opened.current.ucon === ucon && opened.current.style === style;
        keepShownDesign(same ? opened.current!.design : { ucon, style });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, cart.adminDesign, cart.designStyle, data.uconScript, params.parameters]);

    return null;
};

export default ShownDesignKeeper;
